import { useEffect, useState } from "react";
import { Search, Globe } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StoryCard from "../components/StoryCard";
import { Loading } from "../components/Layout";
import { Input } from "../components/ui/input";

export default function Discover() {
  const { region, regions } = useAuth();
  const [stories, setStories] = useState(null);
  const [meta, setMeta] = useState({ categories: [], languages: [] });
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    if (region) params.region = region;
    const { data } = await api.get("/stories", { params });
    setStories(data);
  };

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, cat, region]);

  const regionLabel = (regions.find((r) => r.code === region) || {}).label;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <section className="py-14 sm:py-20 animate-fade-up">
        <span className="uppercase tracking-[0.3em] text-xs text-gold">Premium Audio Boutique</span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight mt-4 max-w-3xl">
          Stories worth staying up for. Told entirely in sound.
        </h1>
        <p className="text-muted-foreground mt-5 max-w-xl leading-relaxed">
          Discover intimate, adults-only audio dramas from independent creators. Preview freely, unlock what moves you,
          one tap at a time.
        </p>
      </section>

      <div className="flex flex-col sm:flex-row gap-4 mb-10 sticky top-16 z-30 py-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <Input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search stories..."
            className="pl-10 bg-card/60 border-white/10 focus-visible:ring-primary"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            data-testid="filter-all"
            onClick={() => setCat("")}
            className={`shrink-0 px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
              cat === "" ? "bg-primary border-primary" : "border-white/10 text-muted-foreground hover:text-foreground"
            }`}
          >
            All
          </button>
          {meta.categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`shrink-0 px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${
                cat === c ? "bg-primary border-primary" : "border-white/10 text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {stories === null ? (
        <Loading />
      ) : stories.length === 0 ? (
        <p className="text-muted-foreground py-20 text-center">No stories found. Try another search.</p>
      ) : (
        <>
          {region && region !== "GLOBAL" && regionLabel && !q && !cat && (
            <p data-testid="region-note" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              <Globe size={13} strokeWidth={1.5} className="text-gold" /> Showing {regionLabel} first
            </p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 sm:gap-6" data-testid="stories-grid">
            {stories.map((s, i) => (
              <StoryCard key={s.id} story={s} featured={i === 0} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
